export type PlateStrategy = 'paired' | 'greedy';
export type PlateUnit = 'kg' | 'lb';

type PlateSystem = {
  unit: PlateUnit;
  barWeight: number;
  plates: number[];
  preference: number[];
  unitScale: number;
};

export type BarbellStackView = {
  unit: PlateUnit;
  total: number;
  plates: number[];
  barWeight: number;
  sourceWeightKg: number | null;
};

export const KG_PLATE_STYLE: Record<number, { className: string; width: number; height: number }> = {
  25: { className: 'bg-red-600 border-red-700', width: 9, height: 14 },
  20: { className: 'bg-blue-600 border-blue-700', width: 8, height: 13 },
  15: { className: 'bg-yellow-400 border-yellow-500', width: 7, height: 12 },
  10: { className: 'bg-green-600 border-green-700', width: 6, height: 11 },
  5: { className: 'bg-white border-gray-400', width: 5, height: 10 },
  2.5: { className: 'bg-gray-900 border-black', width: 4, height: 9 },
  1.25: { className: 'bg-gray-300 border-gray-400', width: 3, height: 8 },
};

export const LB_PLATE_STYLE: Record<number, { className: string; width: number; height: number }> = {
  45: { className: 'bg-red-600 border-red-700', width: 9, height: 14 },
  35: { className: 'bg-blue-600 border-blue-700', width: 8, height: 13 },
  25: { className: 'bg-yellow-400 border-yellow-500', width: 7, height: 12 },
  15: { className: 'bg-green-600 border-green-700', width: 6, height: 11 },
  10: { className: 'bg-white border-gray-400', width: 5, height: 10 },
  5: { className: 'bg-gray-900 border-black', width: 4, height: 9 },
  2.5: { className: 'bg-gray-300 border-gray-400', width: 3, height: 8 },
};

const METRIC_SYSTEM: PlateSystem = {
  unit: 'kg',
  barWeight: 20,
  plates: [25, 20, 15, 10, 5, 2.5, 1.25],
  preference: [20, 10, 15, 25, 5, 2.5, 1.25],
  unitScale: 4,
};

export const METRIC_PLATE_WEIGHTS = METRIC_SYSTEM.plates;

const LB_WOMEN_SYSTEM: PlateSystem = {
  unit: 'lb',
  barWeight: 35,
  plates: [45, 35, 25, 15, 10, 5, 2.5],
  preference: [25, 35, 45, 15, 10, 5, 2.5],
  unitScale: 2,
};

const LB_MEN_SYSTEM: PlateSystem = {
  unit: 'lb',
  barWeight: 45,
  plates: [45, 35, 25, 15, 10, 5, 2.5],
  preference: [25, 35, 45, 15, 10, 5, 2.5],
  unitScale: 2,
};

function weightToUnits(weight: number, unitScale: number): number {
  return Math.round(weight * unitScale);
}

function unitsToWeight(units: number, unitScale: number): number {
  return Math.round((units / unitScale) * 100) / 100;
}

function comparePlatePreference(
  aUnits: number[],
  bUnits: number[],
  preferenceUnits: number[]
): number {
  const counts = (arr: number[]) => {
    const map = new Map<number, number>();
    arr.forEach((u) => map.set(u, (map.get(u) ?? 0) + 1));
    return map;
  };

  const aCounts = counts(aUnits);
  const bCounts = counts(bUnits);
  for (const u of preferenceUnits) {
    const diff = (aCounts.get(u) ?? 0) - (bCounts.get(u) ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

function bestPlateComboWeights(targetUnits: number, system: PlateSystem): number[] | null {
  const memo = new Map<number, number[] | null>();
  const plateUnits = system.plates.map((plate) => weightToUnits(plate, system.unitScale));
  const preferenceUnits = system.preference.map((plate) =>
    weightToUnits(plate, system.unitScale)
  );

  const bestFor = (remaining: number): number[] | null => {
    if (remaining === 0) return [];
    if (remaining < 0) return null;
    if (memo.has(remaining)) return memo.get(remaining)!;

    let best: number[] | null = null;
    for (const currentPlateUnits of plateUnits) {
      if (currentPlateUnits > remaining) continue;
      const next = bestFor(remaining - currentPlateUnits);
      if (!next) continue;
      const candidate = [currentPlateUnits, ...next];
      if (!best || candidate.length < best.length) {
        best = candidate;
        continue;
      }
      if (candidate.length > best.length) continue;
      if (comparePlatePreference(candidate, best, preferenceUnits) > 0) {
        best = candidate;
      }
    }

    memo.set(remaining, best);
    return best;
  };

  return bestFor(targetUnits)?.map((units) => unitsToWeight(units, system.unitScale)) ?? null;
}

function calcPlateStack(total: number, strategy: PlateStrategy, system: PlateSystem): number[] | null {
  if (!Number.isFinite(total)) return null;
  if (total < system.barWeight - 0.001) return null;

  const perSide = (total - system.barWeight) / 2;
  if (perSide <= 0.001) return [];

  const targetUnits = weightToUnits(perSide, system.unitScale);
  if (Math.abs(unitsToWeight(targetUnits, system.unitScale) - perSide) > 0.001) {
    return null;
  }

  if (strategy === 'greedy') {
    let remaining = targetUnits;
    const plates: number[] = [];
    const plateUnits = system.plates.map((plate) => weightToUnits(plate, system.unitScale));
    for (let index = 0; index < plateUnits.length; index += 1) {
      const currentPlateUnits = plateUnits[index];
      while (remaining >= currentPlateUnits) {
        plates.push(unitsToWeight(currentPlateUnits, system.unitScale));
        remaining -= currentPlateUnits;
      }
    }
    if (remaining !== 0) return null;
    return plates;
  }

  return bestPlateComboWeights(targetUnits, system);
}

function parseSpecificLbWodLoad(
  rawLabel?: string | null
): { totalLb: number; barWeight: number; approxKg: number | null } | null {
  const raw = String(rawLabel ?? '').trim();
  if (!raw) return null;

  const womenDirect = raw.match(/^\s*Women(?:\s*RX)?\s*:\s*(\d+(?:\.\d+)?)\s*lb\b/i);
  if (womenDirect?.[1]) {
    return {
      totalLb: Number(womenDirect[1]),
      barWeight: LB_WOMEN_SYSTEM.barWeight,
      approxKg: extractFirstWeight(raw, 'kg'),
    };
  }

  const menDirect = raw.match(/^\s*Men(?:\s*RX)?\s*:\s*(\d+(?:\.\d+)?)\s*lb\b/i);
  if (menDirect?.[1]) {
    return {
      totalLb: Number(menDirect[1]),
      barWeight: LB_MEN_SYSTEM.barWeight,
      approxKg: extractFirstWeight(raw, 'kg'),
    };
  }

  return null;
}

function extractFirstWeight(raw: string, unit: 'kg' | 'lb'): number | null {
  const match = raw.match(new RegExp(`(\\d+(?:\\.\\d+)?)\\s*${unit}\\b`, 'i'));
  if (!match?.[1]) return null;
  const weight = Number(match[1]);
  return Number.isFinite(weight) ? weight : null;
}

function selectLbSystem(barWeight: number): PlateSystem | null {
  if (Math.abs(barWeight - LB_WOMEN_SYSTEM.barWeight) < 0.001) return LB_WOMEN_SYSTEM;
  if (Math.abs(barWeight - LB_MEN_SYSTEM.barWeight) < 0.001) return LB_MEN_SYSTEM;
  return null;
}

export function resolveBarbellStackView(args: {
  totalKg: number;
  strategy: PlateStrategy;
  rxLoadLabel?: string | null;
}): BarbellStackView | null {
  const lbLoad = parseSpecificLbWodLoad(args.rxLoadLabel);
  const shouldUseLbRx =
    !!lbLoad &&
    (lbLoad.approxKg == null || Math.abs(args.totalKg - lbLoad.approxKg) < 0.6);

  if (shouldUseLbRx) {
    const lbSystem = selectLbSystem(lbLoad.barWeight);
    if (lbSystem) {
      const lbPlates = calcPlateStack(lbLoad.totalLb, args.strategy, lbSystem);
      if (lbPlates) {
        return {
          unit: 'lb',
          total: lbLoad.totalLb,
          plates: lbPlates,
          barWeight: lbSystem.barWeight,
          sourceWeightKg: args.totalKg,
        };
      }
    }
  }

  const metric = calcPlateStack(args.totalKg, args.strategy, METRIC_SYSTEM);
  if (!metric) return null;

  return {
    unit: 'kg',
    total: args.totalKg,
    plates: metric,
    barWeight: METRIC_SYSTEM.barWeight,
    sourceWeightKg: args.totalKg,
  };
}

export function formatBarbellStackLabel(view: BarbellStackView): string {
  const perSide = Math.round(view.plates.reduce((sum, plate) => sum + plate, 0) * 100) / 100;
  const unit = view.unit;

  if (view.unit === 'lb' && Number.isFinite(view.sourceWeightKg)) {
    if (view.plates.length === 0) {
      return `${view.sourceWeightKg} kg Rx (${view.total} lb, ${view.barWeight} lb bar)`;
    }
    return `${view.sourceWeightKg} kg Rx (${view.total} lb): ${view.plates.join(' + ')} per side (${view.barWeight} lb bar, ${perSide} lb/side)`;
  }

  if (view.plates.length === 0) return `Bar ${view.barWeight} ${unit}`;
  return `${view.total} ${unit}: ${view.plates.join(' + ')} per side (${view.barWeight} ${unit} bar, ${perSide} ${unit}/side)`;
}

export function isLikelyBarbellExerciseName(name?: string | null): boolean {
  const value = String(name ?? '').trim().toLowerCase();
  if (!value) return false;
  if (
    /(dumbbell|kettlebell|med ball|medicine ball|wall ball|smith machine|trap bar|hex bar|landmine|pvc|cable|machine|sled)/i.test(
      value
    )
  ) {
    return false;
  }

  return (
    /\bbarbell\b/i.test(value) ||
    /\bdeadlift\b/i.test(value) ||
    /\bbench press\b/i.test(value) ||
    /\bthruster\b/i.test(value) ||
    /\boverhead squat\b/i.test(value) ||
    /\bpush jerk\b/i.test(value) ||
    /\bpush press\b/i.test(value) ||
    /\bclean(?:\s*&\s*jerk)?\b/i.test(value) ||
    /\bhang clean\b/i.test(value) ||
    /\bpower clean\b/i.test(value) ||
    /\bsquat clean\b/i.test(value) ||
    /\bsnatch\b/i.test(value) ||
    /\bcluster\b/i.test(value)
  );
}

export function hasBarbellEquipment(equipment: unknown): boolean {
  return Array.isArray(equipment)
    ? equipment.some((item) => String(item).toLowerCase().includes('barbell'))
    : false;
}
